import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Props:
 * - images: string[] (required)
 * - intervalMs: number = 4000  (time each slide stays)
 * - transitionMs: number = 700 (crossfade duration)
 * - heightClass: string = "h-[420px]" (Tailwind height for the gallery)
 * - pauseOnHover: boolean = true
 */
const RotatingGallery = ({
  images = [],
  intervalMs = 4000,
  transitionMs = 700,
  heightClass = "h-[420px]",
  pauseOnHover = true,
  className = "",
}) => {
  const [idx, setIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const timerRef = useRef(null);

  // Preload images once
  useEffect(() => {
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, [images]);

  // Respect reduced motion
  const userPrefersReducedMotion = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }, []);

  // Auto-advance
  useEffect(() => {
    if (!images.length) return;
    if (userPrefersReducedMotion) return; // don’t autoplay if reduced motion
    if (isPaused && pauseOnHover) return;

    timerRef.current = setInterval(() => {
      setIdx((i) => (i + 1) % images.length);
    }, intervalMs);

    return () => clearInterval(timerRef.current);
  }, [images.length, intervalMs, isPaused, pauseOnHover, userPrefersReducedMotion]);

  // Keyboard nav (optional)
  useEffect(() => {
    const onKey = (e) => {
      if (!images.length) return;
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [images.length]);

  if (!images.length) return null;

  return (
    <section
      className={`relative mx-auto w-full max-w-screen-2xl ${heightClass} bg-white overflow-hidden ${className}`}
      onMouseEnter={() => pauseOnHover && setIsPaused(true)}
      onMouseLeave={() => pauseOnHover && setIsPaused(false)}
      aria-label="Rotating gallery"
    >
      {/* Stack all slides, crossfade by opacity */}
      {images.map((src, i) => (
        <img
          key={src + i}
          src={src}
          alt=""
          className="absolute inset-0 w-full h-full object-cover select-none"
          style={{
            opacity: i === idx ? 1 : 0,
            transition: `opacity ${userPrefersReducedMotion ? 0 : transitionMs}ms ease`,
          }}
          draggable={false}
        />
      ))}

      {/* Minimal black text controls/indicators */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((_, i) => (
          <button
            key={i}
            onClick={() => setIdx(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-2 w-2 rounded-full border border-black ${
              i === idx ? "bg-black" : "bg-white"
            }`}
          />
        ))}
      </div>

      {/* Prev/Next (optional, simple black text buttons) */}
      <button
        onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
        className="absolute left-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-white text-black border border-black"
        aria-label="Previous slide"
      >
        ‹
      </button>
      <button
        onClick={() => setIdx((i) => (i + 1) % images.length)}
        className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-1 bg-white text-black border border-black"
        aria-label="Next slide"
      >
        ›
      </button>
    </section>
  );
};

export default RotatingGallery;

